import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Trash2, 
  Eye, 
  Edit, 
  Package, 
  PackageCheck,
  LayoutGrid,
  LayoutList
} from 'lucide-react';
import Header from '../components/layout/Header';
import MaterialDeliveryModal from '../components/warehouse/MaterialDeliveryModal';
import MaterialDeliveryPreviewModal from '../components/warehouse/MaterialDeliveryPreviewModal';
import { useAuth } from '../context/AuthContext';
import { materialDeliveries } from '../services/api';
import ViewSwitch from '../utils/ViewSwitch';

// MaterialCard Component
const MaterialCard = ({ delivery, onPreview, onEdit, onDelete, isAdmin, canManageDeliveries, formatDate }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'InProgress':
        return 'bg-yellow-100 text-yellow-800';
      case 'Completed':
        return 'bg-green-100 text-green-800';
      case 'Cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 p-4 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            #{delivery.deliveryNumber}
          </h3>
          <p className="text-sm text-gray-500">{formatDate(delivery.date)}</p>
          {delivery.projectId && (
            <p className="text-xs text-gray-500 mt-1">
              Project: #{delivery.projectId.contractNumber}
            </p>
          )}
        </div>
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(delivery.status)}`}>
          {delivery.status}
        </span>
      </div>

      {/* Warehouse Info */}
      <div className="space-y-3">
        <div>
          <div className="text-sm font-medium text-gray-600 mb-1">Source Warehouse</div>
          <p className="text-sm text-gray-900">{delivery.sourceWarehouse.name}</p>
        </div>

        <div>
          <div className="text-sm font-medium text-gray-600 mb-1">Destination</div>
          {delivery.isCustomerDelivery ? (
            <div className="text-sm">
              <p className="text-gray-900">{delivery.customerId?.name}</p>
              {delivery.customerId?.isCompany && delivery.customerId?.taxId && (
                <p className="text-xs text-gray-500">Tax ID: {delivery.customerId.taxId}</p>
              )}
              {delivery.customerId?.address && (
                <p className="text-xs text-gray-500 mt-1">
                  {delivery.customerId.address.street},
                  {' '}{delivery.customerId.address.city} {delivery.customerId.address.postalCode}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-900">
              {delivery.targetWarehouse?.name || 'Unknown Warehouse'}
            </p>
          )}
        </div>
      </div>

      {/* Items */}
      <div>
        <div className="text-sm font-medium text-gray-600 mb-2">
          Items ({delivery.items.length})
        </div>
        <div className="space-y-1">
          {delivery.items.slice(0, 2).map((item, index) => (
            <div key={index} className="text-sm text-gray-700">
              <span className="font-medium">{item.item.name}:</span>
              {' '}{item.quantity} {item.item.amountType}
            </div>
          ))}
          {delivery.items.length > 2 && (
            <div className="text-xs text-blue-600">
              + {delivery.items.length - 2} more items
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-2 pt-3 border-t">
        <button
          onClick={() => onPreview(delivery._id)}
          className="p-2 text-blue-600 hover:text-blue-900 rounded-full hover:bg-blue-50"
          title="View Details"
        >
          <Eye className="h-5 w-5" />
        </button>
        
        {delivery.status === 'InProgress' && canManageDeliveries && (
          <button
            onClick={() => onEdit(delivery._id)}
            className="p-2 text-blue-600 hover:text-blue-900 rounded-full hover:bg-blue-50"
            title="Edit Delivery"
          >
            <Edit className="h-5 w-5" />
          </button>
        )}

        {isAdmin && delivery.status !== 'InProgress' && (
          <button
            onClick={() => onDelete(delivery._id)}
            className="p-2 text-red-600 hover:text-red-900 rounded-full hover:bg-red-50"
            title="Delete Delivery"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  );
};

// Main Page Component
const MaterialDeliveryPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [deliveries, setDeliveries] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCardView, setIsCardView] = useState(window.innerWidth < 768);
  const { user, isAdmin, isOfficeAdmin, isWarehouseAdmin } = useAuth();

  const canManageDeliveries = isAdmin() || isOfficeAdmin() || isWarehouseAdmin();

  useEffect(() => {
    fetchDeliveries();
    
    // Handle window resize for responsive view
    const handleResize = () => {
      if (window.innerWidth < 640 && !isCardView) {
        setIsCardView(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchDeliveries = async () => {
    try {
      const { data } = await materialDeliveries.getAll();
      setDeliveries(data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load deliveries');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateDelivery = async (deliveryData) => {
    try {
      await materialDeliveries.create(deliveryData);
      fetchDeliveries();
      setIsModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create delivery');
    }
  };

  const handleDeleteDelivery = async (id) => {
    if (!window.confirm('Are you sure you want to delete this delivery?')) {
      return;
    }

    try {
      await materialDeliveries.delete(id);
      fetchDeliveries();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete delivery');
    }
  };

  const handlePreviewDelivery = async (id) => {
    try {
      const { data } = await materialDeliveries.getById(id);
      setSelectedDelivery(data);
      setIsPreviewModalOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load delivery details');
    }
  };

  const handleEditDelivery = async (id) => {
    try {
      const { data } = await materialDeliveries.getById(id);
      setSelectedDelivery(data);
      setIsModalOpen(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load delivery details');
    }
  };

  const handleUpdateDelivery = async (deliveryData) => {
    try {
      const { data } = await materialDeliveries.update(selectedDelivery._id, deliveryData);
      setDeliveries(prevDeliveries => 
        prevDeliveries.map(delivery => 
          delivery._id === data._id ? data : delivery
        )
      );
      setIsModalOpen(false);
      setSelectedDelivery(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update delivery');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredDeliveries = deliveries.filter(delivery =>
    delivery.deliveryNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    delivery.sourceWarehouse?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (delivery.targetWarehouse?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (delivery.customerId?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (delivery.projectId?.contractNumber || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Material Deliveries</h2>
            {canManageDeliveries && (
              <button
                onClick={() => {
                  setSelectedDelivery(null);
                  setIsModalOpen(true);
                }}
                className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-5 w-5 mr-2" />
                New Delivery
              </button>
            )}
          </div>

          {/* Search and View Toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative flex-grow max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search deliveries..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <ViewSwitch isCardView={isCardView} onViewChange={setIsCardView} />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {isCardView ? (
          // Card View
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDeliveries.map((delivery) => (
              <MaterialCard
                key={delivery._id}
                delivery={delivery}
                onPreview={handlePreviewDelivery}
                onEdit={handleEditDelivery}
                onDelete={handleDeleteDelivery}
                isAdmin={isAdmin()}
                canManageDeliveries={canManageDeliveries}
                formatDate={formatDate}
              />
            ))}
          </div>
        ) : (
          // Table View
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Delivery Number
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Source Warehouse
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Destination
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Items
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDeliveries.map((delivery) => (
                    <tr key={delivery._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          #{delivery.deliveryNumber}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          delivery.status === 'InProgress' ? 'bg-yellow-100 text-yellow-800' :
                          delivery.status === 'Completed' ? 'bg-green-100 text-green-800' :
                          delivery.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {delivery.status}
                        </span>
                        {delivery.projectId && (
                          <div className="text-xs text-gray-500 mt-1">
                            Project: #{delivery.projectId.contractNumber}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {formatDate(delivery.date)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {delivery.sourceWarehouse.name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {delivery.isCustomerDelivery ? (
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {delivery.customerId?.name || 'Unknown Customer'}
                            </div>
                            {delivery.customerId?.isCompany && delivery.customerId?.taxId && (
                              <div className="text-xs text-gray-500">
                                Tax ID: {delivery.customerId.taxId}
                              </div>
                            )}
                            {delivery.customerId?.address && (
                              <>
                                <div className="text-xs text-gray-500">
                                  {delivery.customerId.address.street}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {delivery.customerId.address.postalCode} {delivery.customerId.address.city}
                                </div>
                              </>
                            )}
                          </div>
                        ) : (
                          <div className="text-sm text-gray-900">
                            {delivery.targetWarehouse?.name || 'Unknown Warehouse'}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {delivery.items.length} items
                          <div className="text-xs text-gray-500 mt-1">
                            {delivery.items.slice(0, 2).map((item, index) => (
                              <div key={index}>
                                {item.item.name}: {item.quantity} {item.item.amountType}
                              </div>
                            ))}
                            {delivery.items.length > 2 && (
                              <div className="text-xs text-blue-500">
                                + {delivery.items.length - 2} more items
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handlePreviewDelivery(delivery._id)}
                            className="text-blue-600 hover:text-blue-900"
                            title="View Delivery"
                          >
                            <Eye className="h-5 w-5" />
                          </button>
                          {delivery.status === 'InProgress' && canManageDeliveries && (
                            <button
                              onClick={() => handleEditDelivery(delivery._id)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Edit Delivery"
                            >
                              <Edit className="h-5 w-5" />
                            </button>
                          )}
                          {isAdmin() && delivery.status !== 'InProgress' && (
                            <button
                              onClick={() => handleDeleteDelivery(delivery._id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Delivery"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {filteredDeliveries.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No deliveries found matching your search criteria.</p>
          </div>
        )}

        {/* Modals */}
        {isModalOpen && (
          <MaterialDeliveryModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedDelivery(null);
            }}
            onSave={selectedDelivery ? handleUpdateDelivery : handleCreateDelivery}
            delivery={selectedDelivery}
          />
        )}

        {isPreviewModalOpen && selectedDelivery && (
          <MaterialDeliveryPreviewModal
            isOpen={isPreviewModalOpen}
            onClose={() => {
              setIsPreviewModalOpen(false);
              setSelectedDelivery(null);
            }}
            delivery={selectedDelivery}
          />
        )}
      </main>
    </div>
  );
};

export default MaterialDeliveryPage;